import { cloneDeep } from 'lodash'
import { sort } from 'timsort'

import {
    ChannelState,
    DataNavigationCalculatedEvent,
    DataNavigationEvent,
    DataNavigationState
} from 'plugin-live-exploratory-viz/src/data-navigation/types/widget'
import {
    checkSpan,
    filterChannelsByLayer,
    getCalculatedChannelNames,
    getMultiChannelIdentifierWithLayerPrefixFromLayerByMnemonic,
    sortCompare
} from 'plugin-live-exploratory-viz/src/data-navigation/reducer/utils'
import { shouldDelayClearState } from 'plugin-live-exploratory-viz/src/data-navigation/reducer'

export function onEventQuery(
    message: EventFlowMessage<DataNavigationEvent & DataNavigationCalculatedEvent>,
    state: DataNavigationState
): DataNavigationState {
    const unsorted = new Set<string>()
    const series: Record<string, ChannelState> = {}

    message.events.forEach(ev => {
        const { timestamp, mnemonic } = ev
        const layer = message.layer?.toString()
        // TODO: check count: number events
        const isCalculatedEvent = !mnemonic
        if (isCalculatedEvent) {
            const calculatedChannelNames = getCalculatedChannelNames(ev)
            if (!calculatedChannelNames) {
                return
            }
            calculatedChannelNames.forEach(calcChannelName => {
                processEvent(
                    state.series,
                    series,
                    unsorted,
                    ev[calcChannelName],
                    timestamp,
                    layer,
                    state.span,
                    calcChannelName,
                    shouldDelayClearState.enabled
                )
            })
        } else {
            processEvent(
                state.series,
                series,
                unsorted,
                ev?.value,
                timestamp,
                layer,
                state.span,
                mnemonic,
                shouldDelayClearState.enabled
            )
        }
    })

    // sort all unsorted datasets
    unsorted.forEach(mnemonic => {
        sort(series[mnemonic].data, sortCompare)
    })

    if (shouldDelayClearState.enabled) {
        shouldDelayClearState.enabled = false
    }

    return {
        config: cloneDeep(state.config),
        realtime: state.realtime,
        series: { ...state.series, ...series },
        span: state.span,
        shouldDelayClearState: shouldDelayClearState
    }
}

const processEvent = (
    originalSeries: Record<string, ChannelState>,
    series: Record<string, ChannelState>,
    unsorted: Set<string>,
    value: number,
    timestamp: number,
    layer: string,
    span: [number, number],
    channelName: string,
    shouldDelayClearState: boolean
): void => {
    const multiChannelIdentifierWithLayerPrefix = getMultiChannelIdentifierWithLayerPrefixFromLayerByMnemonic(
        filterChannelsByLayer(originalSeries, layer),
        channelName
    )

    if (shouldDelayClearState) {
        originalSeries[multiChannelIdentifierWithLayerPrefix] = {
            data: [],
            domain: [0, 0]
        }
    }

    const serie =
        !(multiChannelIdentifierWithLayerPrefix in series) &&
        (originalSeries?.[multiChannelIdentifierWithLayerPrefix]?.data?.length ?? 0) > 0
            ? (series[multiChannelIdentifierWithLayerPrefix] = cloneDeep(
                  originalSeries[multiChannelIdentifierWithLayerPrefix]
              ))
            : multiChannelIdentifierWithLayerPrefix in series
            ? series[multiChannelIdentifierWithLayerPrefix]
            : null
    // out-of-span: don't append on state.
    const end = span[1] + (span[1] - span[0]) * 0.1

    if (timestamp < span[0] || timestamp > end) {
        return
    }

    if (!serie) {
        series[multiChannelIdentifierWithLayerPrefix] = {
            data: checkSpan(timestamp, span) ? [[timestamp, value]] : [],
            domain: [value, value]
        }
    } else {
        const data = serie.data
        // out-of-order events, please sort it at the end.
        if (data.length > 0 && timestamp < data[data.length - 1][0]) {
            unsorted.add(multiChannelIdentifierWithLayerPrefix)
        }
        // update domain.
        if (value < serie.domain[0]) {
            serie.domain[0] = value
        } else if (value > serie.domain[1]) {
            serie.domain[1] = value
        }
        data.push([timestamp, value])

        series[multiChannelIdentifierWithLayerPrefix] = serie
    }
}
